import { ClarionToasterProvider } from '@clarion/ClarionToaster.tsx';
import { ErrorBoundary } from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CSSProperties, ReactNode, Suspense } from 'react';
import { RouterProvider as AriaRouterProvider } from 'react-aria-components';
import { HotkeysProvider } from 'react-hotkeys-hook';
import {
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  useHref,
  useNavigate,
} from 'react-router';
import { RouterProvider } from 'react-router/dom';
import RequireAuth from './auth/RequireAuth';
import RequireEula from './auth/RequireEula';
import RequireWorkspace from './auth/RequireWorkspace';
import { UserStateProvider } from './auth/UserState';
import { WorkspaceProvider } from './auth/WorkspaceState';
import { MobileTopNavigation } from './components/navigation/MobileTopNavigation.tsx';
import { SideNavigation } from './components/navigation/SideNavigation.tsx';
import SkipLink from './components/navigation/SkipLink.tsx';
import { EmbedErrorBoundary } from './embed/EmbedErrorBoundary';
import GoogleAnalytics from './GoogleAnalytics.tsx';
import HomeRouter from './home/HomeRouter';
import HubSpotAnalytics from './HubSpotAnalytics.tsx';
import { sentryCreateBrowserRouter } from './sentry.ts';
import EmbedLoadingScreen from './shared/EmbedLoadingScreen';
import { GlobalSpinner, NavigationSpinner } from './shared/GlobalSpinner.tsx';
import NewVersionToast from './shared/NewVersionToast';
import OfflineStatusToast from './shared/OfflineStatusToast';
import RouteScrollToTop from './shared/RouteScrollToTop';
import supportLinks from './shared/supportLinks.ts';
import { AvailableLanguageProvider } from './translation/AvailableLanguageProvider';
import { ErrorView, RouterErrorBoundary } from './util/ErrorBoundary';
import { getFlag } from './util/flags';
import HotkeysDialog from './util/HotkeysDialog.tsx';
import { HotkeysDialogProvider } from './util/HotkeysDialogContext.tsx';
import useCloudFunctionWarmer from './util/useCloudFunctionWarmer.ts';
import {
  RequireIntegrationAccess,
  RequireNonIntegrationReviewer,
} from './workspace/RequireIntegrationAccess';

function RecapProviders({ children }: { children: ReactNode }) {
  useCloudFunctionWarmer();

  return (
    <UserStateProvider>
      <WorkspaceProvider>
        <AvailableLanguageProvider>{children}</AvailableLanguageProvider>
      </WorkspaceProvider>
    </UserStateProvider>
  );
}

function EmbeddedLayout() {
  useCloudFunctionWarmer();

  return (
    <AvailableLanguageProvider>
      <Suspense fallback={<EmbedLoadingScreen />}>
        {/* eslint-disable-next-line local-rules/no-main-element -- this is the exception as a root layout */}
        <main
          className="w-full min-h-full flex flex-col relative"
          style={{ '--header-height': '0px' } as CSSProperties}
        >
          <NavigationSpinner />
          <Outlet />
        </main>
        <OfflineStatusToast />
        <NewVersionToast />
      </Suspense>
    </AvailableLanguageProvider>
  );
}

function RootLayout() {
  return (
    <RecapProviders>
      <GoogleAnalytics />
      <HubSpotAnalytics />
      <SkipLink />
      <SideNavigation>
        <MobileTopNavigation />
        {/* eslint-disable-next-line local-rules/no-main-element -- this is the exception as a root layout */}
        <main id="main" className="min-h-full min-w-0 flex-1">
          <Suspense fallback={<GlobalSpinner />}>
            <NavigationSpinner />
            <Outlet />
          </Suspense>
        </main>
      </SideNavigation>
      <OfflineStatusToast />
      <NewVersionToast />
      <RouteScrollToTop />
    </RecapProviders>
  );
}

// Workaround for external links. See https://github.com/adobe/react-spectrum/issues/6397#issuecomment-2256960329
function useAbsoluteHref(path: string) {
  const relative = useHref(path);
  if (
    path.startsWith('https://') ||
    path.startsWith('http://') ||
    path.startsWith('mailto:') ||
    path.startsWith('tel:')
  ) {
    return path;
  }
  return relative;
}

function AriaProvider() {
  const navigate = useNavigate();
  return (
    <AriaRouterProvider navigate={navigate} useHref={useAbsoluteHref}>
      <Outlet />
      <ClarionToasterProvider />
    </AriaRouterProvider>
  );
}

const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <Route element={<AriaProvider />}>
      {/* Embedded transcript viewer needs to be outside the regular Providers and navigation */}
      <Route
        path="/embed/*"
        element={<EmbeddedLayout />}
        ErrorBoundary={EmbedErrorBoundary}
        HydrateFallback={GlobalSpinner}
      >
        <Route path=":workspaceId/showcases/:showcaseId/*">
          <Route path="" lazy={() => import('./embed/EmbeddedShowcaseDetails.tsx')} />
          <Route path="search" lazy={() => import('./embed/EmbeddedShowcaseSearch.tsx')} />
          <Route path="files/:fileId" lazy={() => import('./embed/EmbeddedFileDetailsPage.tsx')} />
        </Route>

        <Route
          path=":workspaceId/files/:fileId"
          lazy={() => import('./embed/EmbeddedFileDetailsPage.tsx')}
        />

        <Route path="live/stream/:streamId" lazy={() => import('./streams/PublicStreamPage.tsx')} />
        <Route
          path="live/course/:workspaceId/:courseId"
          lazy={() => import('./streams/PublicCourseStreamPage.tsx')}
        />

        {/* Catch-all for invalid embed routes */}
        <Route path="*" lazy={() => import('./embed/EmbedNotFound.tsx')} />
      </Route>

      <Route
        element={<RootLayout />}
        ErrorBoundary={RouterErrorBoundary}
        HydrateFallback={GlobalSpinner}
      >
        {/* This route is just to keep the RootLayout visible (if possible) when an error is thrown) */}
        <Route element={<Outlet />} ErrorBoundary={RouterErrorBoundary}>
          <Route path="/" element={<HomeRouter />} />

          <Route path="/licenses" lazy={() => import('./licenses/LicensesPage')} />
          <Route path="/login" lazy={() => import('./auth/AuthPage')} />
          <Route path="/signup" lazy={() => import('./auth/AuthPage')} />
          <Route path="/auth" lazy={() => import('./auth/AuthPage')} />

          <Route element={<RequireAuth />}>
            <Route element={<RequireEula />}>
              <Route
                path="/workspaces/:workspaceId/join"
                lazy={() => import('./workspace/WorkspaceJoinPage')}
              />
              <Route path="/workspaces/:workspaceId/*" element={<RequireWorkspace />}>
                <Route path="" element={<Navigate replace to="files" />} />
                <Route path="files/recap" lazy={() => import('./files/FilesPage')} />
                <Route
                  path="files/recap/:fileId"
                  lazy={() => import('./details/RecapDetailsPage')}
                />
                <Route
                  path="settings"
                  lazy={() => import('./workspace/admin/WorkspaceSettingsWrapper')}
                  ErrorBoundary={RouterErrorBoundary}
                >
                  <Route path="" element={<Navigate to="general" replace />} />
                  <Route path="general" lazy={() => import('./workspace/admin/GeneralPage')} />
                  <Route
                    path="delete"
                    lazy={() => import('./workspace/admin/DeleteWorkspacePage')}
                  />
                  <Route path="members" lazy={() => import('./workspace/admin/MembersPage')} />
                  <Route
                    path="integrations"
                    lazy={() => import('./workspace/admin/IntegrationsPage')}
                  />
                  <Route
                    path="dictionaries"
                    lazy={() => import('./workspace/admin/DictionariesPage')}
                  />
                  {getFlag('captionAudit') && (
                    <Route
                      path="caption-audit"
                      lazy={() => import('./integrations/captionAudit/CaptionAuditDashboard.tsx')}
                    />
                  )}
                  {getFlag('captionAudit') && (
                    <Route
                      path="caption-audit/reports/:reportId"
                      lazy={() => import('./integrations/captionAudit/CaptionAuditReportPage.tsx')}
                    />
                  )}
                  <Route element={<RequireNonIntegrationReviewer />}>
                    <Route
                      path="analytics"
                      lazy={() => import('./requests/MediaRequestAnalyticsDashboard')}
                    />
                    <Route path="stats" lazy={() => import('./workspace/WorkspaceStatsPage')} />
                    <Route
                      path="credit"
                      lazy={() => import('./workspace/admin/UsageAndBillingPage')}
                    />
                    <Route
                      path="pricing-calculator"
                      lazy={() => import('./workspace/admin/PricingCalculatorPage')}
                    />
                    <Route
                      path="referrals"
                      lazy={() => import('./workspace/admin/ReferralsPage')}
                    />
                    <Route
                      path="activity-log"
                      lazy={() => import('./workspace/admin/ActivityLogPage')}
                    />
                    <Route path="showcases" lazy={() => import('./showcases/ShowcasesPage')}>
                      <Route
                        ErrorBoundary={RouterErrorBoundary}
                        path=":showcaseId"
                        lazy={() => import('./showcases/ShowcaseDetailsPage')}
                      />
                      <Route path="*" lazy={() => import('./showcases/SelectShowcase')} />
                    </Route>
                  </Route>
                  <Route path="*" lazy={() => import('./util/NotFoundPage')} />
                </Route>
                <Route path="search" lazy={() => import('./search/WorkspaceSearchPage')} />
                <Route path="departments" lazy={() => import('./departments/DepartmentsPage')}>
                  <Route
                    ErrorBoundary={RouterErrorBoundary}
                    path=":departmentId"
                    lazy={() => import('./departments/DepartmentDetailsPage')}
                  />
                </Route>
                <Route
                  path="departments/import"
                  lazy={() => import('./departments/DepartmentImportPage')}
                />
                <Route
                  path="departments/:departmentId/courses/:courseId"
                  lazy={() => import('./departments/CourseDetailsPage')}
                />
                <Route
                  path="corrections"
                  lazy={() => import('./corrections/PendingCorrectionsPage.tsx')}
                >
                  <Route
                    path=":fileId/:correctionId"
                    lazy={() => import('./corrections/ReviewCorrection.tsx')}
                  />
                  <Route path="*" lazy={() => import('./corrections/SelectCorrection.tsx')} />
                </Route>
                <Route
                  path="requests"
                  lazy={() => import('./requests/PendingMediaRequestsPage.tsx')}
                >
                  <Route
                    path=":fileId/:requestId"
                    lazy={() => import('./requests/ReviewRequest.tsx')}
                  />
                  <Route path="*" lazy={() => import('./requests/SelectRequest.tsx')} />
                </Route>
                <Route element={<RequireNonIntegrationReviewer />}>
                  <Route
                    path="assignments"
                    lazy={() => import('./assignments/UserAssignmentsPage')}
                  />
                </Route>

                <Route
                  path="files/panopto"
                  element={<RequireIntegrationAccess integration="panopto" />}
                >
                  <Route
                    path=""
                    lazy={() => import('./integrations/panopto/PanoptoVideosPage.tsx')}
                  />
                  <Route
                    path="orders"
                    lazy={() => import('./integrations/panopto/PanoptoOrderHistory.tsx')}
                  />
                  <Route
                    path=":videoId"
                    lazy={() => import('./integrations/panopto/PanoptoDetailsPage.tsx')}
                  />
                </Route>
                <Route
                  path="files/youtube"
                  element={<RequireIntegrationAccess integration="youtube" />}
                >
                  <Route
                    path=""
                    lazy={() => import('./integrations/youtube/YouTubeVideosPage.tsx')}
                  />
                  <Route
                    path=":videoId"
                    lazy={() => import('./integrations/youtube/YouTubeDetailsPage.tsx')}
                  />
                </Route>
                <Route
                  path="files/googlecalendar"
                  element={<RequireIntegrationAccess integration="googlecalendar" />}
                >
                  <Route
                    path=""
                    lazy={() => import('./integrations/googlecalendar/GoogleCalendarPage.tsx')}
                  />
                </Route>
                <Route
                  path="files/microsoftoutlook"
                  element={<RequireIntegrationAccess integration="microsoftoutlook" />}
                >
                  <Route
                    path=""
                    lazy={() => import('./integrations/microsoftoutlook/MicrosoftOutlookPage.tsx')}
                  />
                </Route>
                <Route
                  path="files/microsoftteams"
                  element={<RequireIntegrationAccess integration="microsoftteams" />}
                >
                  <Route
                    path=""
                    lazy={() => import('./integrations/microsoftteams/MicrosoftTeamsFilesPage.tsx')}
                  />
                  <Route
                    path=":meetingId"
                    lazy={() =>
                      import('./integrations/microsoftteams/MicrosoftTeamsDetailsPage.tsx')
                    }
                  />
                </Route>
                <Route
                  path="files/brightcove"
                  element={<RequireIntegrationAccess integration="brightcove" />}
                >
                  <Route
                    path=""
                    lazy={() => import('./integrations/brightcove/BrightcoveVideosPage.tsx')}
                  />
                  <Route
                    path=":videoId"
                    lazy={() => import('./integrations/brightcove/BrightcoveDetailsPage.tsx')}
                  />
                </Route>
                <Route
                  path="files/cloudflarestream"
                  element={<RequireIntegrationAccess integration="cloudflarestream" />}
                >
                  <Route
                    path=""
                    lazy={() =>
                      import('./integrations/cloudflarestream/CloudflareStreamVideosPage.tsx')
                    }
                  />
                  <Route
                    path=":videoId"
                    lazy={() =>
                      import('./integrations/cloudflarestream/CloudflareStreamDetailsPage.tsx')
                    }
                  />
                </Route>
                <Route
                  path="files/googledrive"
                  element={<RequireIntegrationAccess integration="googledrive" />}
                >
                  <Route
                    path=""
                    lazy={() => import('./integrations/googleDrive/GoogleDriveFilesPage.tsx')}
                  />
                  <Route
                    path=":videoId"
                    lazy={() => import('./integrations/googleDrive/GoogleDriveDetailsPage.tsx')}
                  />
                </Route>
                {/* Google Meet routes redirect to Google Drive's Meet recordings tab */}
                <Route
                  path="files/googlemeet"
                  element={<Navigate to="../files/googledrive?tab=meet" replace />}
                />
                <Route
                  path="files/googlemeet/:conferenceRecordId"
                  element={<Navigate to="../files/googledrive?tab=meet" replace />}
                />
                <Route
                  path="files/googlemeet/:conferenceRecordId/recordings/:recordingId"
                  element={<Navigate to="../files/googledrive?tab=meet" replace />}
                />
                <Route path="files/box" element={<RequireIntegrationAccess integration="box" />}>
                  <Route path="" lazy={() => import('./integrations/box/BoxFilesPage.tsx')} />
                  <Route
                    path=":videoId"
                    lazy={() => import('./integrations/box/BoxDetailsPage.tsx')}
                  />
                </Route>
                <Route
                  path="files/kaltura"
                  element={<RequireIntegrationAccess integration="kaltura" />}
                >
                  <Route
                    path=""
                    lazy={() => import('./integrations/kaltura/KalturaFilesPage.tsx')}
                  />
                  <Route
                    path=":videoId"
                    lazy={() => import('./integrations/kaltura/KalturaEntryDetailsPage.tsx')}
                  />
                  <Route
                    path="reach/jobs"
                    lazy={() => import('./integrations/kaltura/KalturaReachJobHistory.tsx')}
                  />
                  <Route
                    path="reach/billing"
                    lazy={() => import('./integrations/kaltura/KalturaReachBillingReport.tsx')}
                  />
                </Route>
                <Route
                  path="files/webex"
                  element={<RequireIntegrationAccess integration="webex" />}
                >
                  <Route path="" lazy={() => import('./integrations/webex/WebexFilesPage.tsx')} />
                  <Route
                    path=":videoId"
                    lazy={() => import('./integrations/webex/WebexDetailsPage.tsx')}
                  />
                </Route>
                <Route path="files/zoom" element={<RequireIntegrationAccess integration="zoom" />}>
                  <Route path="" lazy={() => import('./integrations/zoom/ZoomFilesPage.tsx')} />
                  <Route
                    path=":videoId"
                    lazy={() => import('./integrations/zoom/ZoomDetailsPage.tsx')}
                  />
                </Route>
                <Route
                  path="files/canvasstudio"
                  element={<RequireIntegrationAccess integration="canvasstudio" />}
                >
                  <Route
                    path=""
                    lazy={() => import('./integrations/canvasStudio/CanvasStudioFilesPage.tsx')}
                  />
                  <Route
                    path=":credentialId"
                    lazy={() => import('./integrations/canvasStudio/CanvasStudioFilesPage.tsx')}
                  />
                  <Route
                    path=":credentialId/media/:mediaId"
                    lazy={() => import('./integrations/canvasStudio/CanvasStudioDetailsPage.tsx')}
                  />
                </Route>
                {/* Fallback for when partner is unknown or existing emails */}
                <Route path="files/:fileId" lazy={() => import('./details/DetailsPageRedirect')} />
                <Route
                  path="files/:fileId/analysis"
                  lazy={() => import('./transcriptAccuracyAnalysis/TranscriptAccuracyAnalysis')}
                />

                <Route
                  path="files/:fileId/voice-ad-studio/:textTrackId?"
                  lazy={() => import('./voiceADStudio/VoiceADStudioPage')}
                />

                <Route path="streams" lazy={() => import('./streams/StreamManager')} />
                <Route
                  path="streams/:streamId"
                  lazy={() => import('./streams/StreamWorkspacePage')}
                />

                <Route
                  path="live-transcription/analytics"
                  lazy={() => import('./liveTranscription/LiveTranscriptionAnalyticsDashboard')}
                />
                <Route
                  path="live-transcription/:sessionId/analytics"
                  lazy={() => import('./liveTranscription/SessionDetailedAnalytics')}
                />

                <Route
                  path="live-transcription/work-orders"
                  lazy={() => import('./liveTranscription/WorkOrderManagement')}
                />

                <Route
                  path="live-transcription/scheduled-events"
                  lazy={() => import('./liveTranscription/ScheduledEventsManagement')}
                />
              </Route>

              <Route path="/organizations/:organizationId">
                <Route path="" element={<Navigate replace to="dashboard" />} />
                <Route
                  path="dashboard"
                  lazy={() => import('./organization/OrganizationDashboard')}
                />
                <Route path="admins" lazy={() => import('./organization/OrganizationAdminsPage')} />
                <Route
                  path="/organizations/:organizationId/analytics"
                  lazy={() => import('./organization/OrganizationAnalyticsPage')}
                />
                <Route
                  path="/organizations/:organizationId/billing"
                  lazy={() => import('./organization/OrganizationBillingPage')}
                />
              </Route>

              <Route path="/account" lazy={() => import('./account/AccountPage')} />

              <Route
                path="/operator/:selectedWorkspaceId?"
                lazy={() => import('./operator/OperatorPage')}
              />
              <Route
                path="/operator/media-requests/analytics"
                lazy={() => import('./operator/MediaRequestOperatorDashboard')}
              />
              <Route
                path="/operator/live-transcription/analytics"
                lazy={() => import('./liveTranscription/LiveTranscriptionOperatorDashboard')}
              />
              <Route path="/operator/stats" lazy={() => import('./operator/OperatorStatsPage')} />
            </Route>
          </Route>

          <Route path="/clarion" lazy={() => import('./clarion/ClarionReferencePage.tsx')} />

          <Route
            path="/help"
            Component={() => {
              window.location.replace(supportLinks.help);
              return null;
            }}
          />

          <Route
            path="/privacy"
            Component={() => {
              window.location.replace(supportLinks.privacy);
              return null;
            }}
          />
          <Route
            path="/terms"
            Component={() => {
              window.location.replace(supportLinks.terms);
              return null;
            }}
          />
          <Route
            path="/about"
            Component={() => {
              window.location.replace(supportLinks.about);
              return null;
            }}
          />

          <Route
            path="/caption-converter"
            lazy={() => import('./captionConverter/CaptionConverterPage')}
          />

          <Route path="*" lazy={() => import('./util/NotFoundPage')} />
        </Route>
      </Route>
    </Route>,
  ),
);

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1 } } });

export default function App() {
  return (
    <ErrorBoundary fallback={({ error }) => <ErrorView error={error} debugInfo={`${error}`} />}>
      <HotkeysProvider>
        <HotkeysDialogProvider>
          <HotkeysDialog />
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </HotkeysDialogProvider>
      </HotkeysProvider>
    </ErrorBoundary>
  );
}
